import type { ReactNode } from "react";
import { Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import type {
  WfRisk,
  WfStatus,
  ExecStatus,
  ExecStepStatus,
  HealthStatus,
} from "@/types";

/* ── Semantic color maps — the shared visual language ─────────────────────── */

export const RISK_META: Record<WfRisk, { label: string; color: string }> = {
  low: { label: "Low", color: "#22c55e" },
  medium: { label: "Medium", color: "#f59e0b" },
  high: { label: "High", color: "#f97316" },
  restricted: { label: "Restricted", color: "#f43f5e" },
};

export const WF_STATUS_META: Record<WfStatus, { label: string; color: string }> = {
  active: { label: "Active", color: "#22c55e" },
  draft: { label: "Draft", color: "#94a3b8" },
  paused: { label: "Paused", color: "#f59e0b" },
  "needs-approval": { label: "Needs Approval", color: "#a855f7" },
  broken: { label: "Broken", color: "#f43f5e" },
};

export const EXEC_STATUS_META: Record<ExecStatus, { label: string; color: string }> = {
  running: { label: "Running", color: "#22d3ee" },
  waiting: { label: "Waiting", color: "#f59e0b" },
  "needs-approval": { label: "Needs Approval", color: "#a855f7" },
  failed: { label: "Failed", color: "#f43f5e" },
  completed: { label: "Completed", color: "#22c55e" },
  paused: { label: "Paused", color: "#94a3b8" },
  cancelled: { label: "Cancelled", color: "#64748b" },
};

export const STEP_STATUS_META: Record<ExecStepStatus, { label: string; color: string }> = {
  pending: { label: "Pending", color: "#64748b" },
  running: { label: "Running", color: "#22d3ee" },
  completed: { label: "Completed", color: "#22c55e" },
  failed: { label: "Failed", color: "#f43f5e" },
  skipped: { label: "Skipped", color: "#64748b" },
  "waiting-approval": { label: "Waiting for Approval", color: "#a855f7" },
  retrying: { label: "Retrying", color: "#f59e0b" },
};

export const HEALTH_META: Record<HealthStatus, { label: string; color: string }> = {
  healthy: { label: "Healthy", color: "#22c55e" },
  degraded: { label: "Degraded", color: "#f59e0b" },
  delayed: { label: "Delayed", color: "#38bdf8" },
  "rate-limited": { label: "Rate Limited", color: "#f97316" },
  offline: { label: "Offline", color: "#f43f5e" },
  "needs-attention": { label: "Needs Attention", color: "#fb7185" },
};

/* ── Pills & badges ───────────────────────────────────────────────────────── */

export function Pill({
  color,
  children,
  dot,
  pulse,
  className,
}: {
  color: string;
  children: ReactNode;
  dot?: boolean;
  pulse?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap",
        className
      )}
      style={{ color, borderColor: `${color}55`, background: `${color}1a` }}
    >
      {dot && (
        <span className="relative flex h-1.5 w-1.5">
          {pulse && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: color }} />
          )}
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: color }} />
        </span>
      )}
      {children}
    </span>
  );
}

export function RiskBadge({ risk, className }: { risk: WfRisk; className?: string }) {
  const m = RISK_META[risk];
  return (
    <Pill color={m.color} className={className}>
      {risk === "restricted" && <Lock size={9} />}
      {m.label}
    </Pill>
  );
}

/* ── Form controls ────────────────────────────────────────────────────────── */

export function Toggle({
  checked,
  onChange,
  color = "#22c55e",
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  color?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="grid h-5 w-9 shrink-0 place-items-start rounded-full p-0.5 transition"
      style={{ background: checked ? color : "hsl(var(--surface-2))" }}
    >
      <span
        className="h-4 w-4 rounded-full bg-white shadow transition-transform"
        style={{ transform: `translateX(${checked ? 16 : 0}px)` }}
      />
    </button>
  );
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  accent = "#f59e0b",
  size = "sm",
}: {
  options: { value: T; label: string; icon?: ReactNode }[];
  value: T;
  onChange: (v: T) => void;
  accent?: string;
  size?: "sm" | "md";
}) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-app bg-surface-2/60 p-0.5">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={cn(
              "flex items-center gap-1.5 rounded-md font-medium transition",
              size === "sm" ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs",
              active ? "text-app" : "text-app-2 hover:text-app-1"
            )}
            style={active ? { background: `${accent}22`, color: accent } : undefined}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium text-app-1">{label}</span>
        {hint && <span className="text-[10px] text-app-2">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

export function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border border-app bg-surface-2 px-3 py-1.5 text-xs text-app outline-none transition focus:border-app-strong placeholder:text-app-2"
    />
  );
}

export function SelectInput({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg border border-app bg-surface-2 px-3 py-1.5 text-xs text-app outline-none transition focus:border-app-strong"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

/** A compact metric tile used in overview rows. */
export function MetricTile({
  label,
  value,
  icon,
  color,
  sub,
  pulse,
}: {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  color: string;
  sub?: ReactNode;
  pulse?: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-app bg-surface/60 p-3.5 backdrop-blur-xl">
      <div
        className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full opacity-20 blur-2xl"
        style={{ background: color }}
      />
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium uppercase tracking-wider text-app-2">{label}</span>
        {icon && (
          <span className="grid h-6 w-6 place-items-center rounded-lg" style={{ background: `${color}22`, color }}>
            {icon}
          </span>
        )}
      </div>
      <div className="mt-1.5 flex items-end gap-2">
        <span className="text-2xl font-semibold leading-none text-app tabular-nums">{value}</span>
        {pulse && (
          <span className="relative mb-1 flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-70" style={{ background: color }} />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: color }} />
          </span>
        )}
      </div>
      {sub && <div className="mt-1 text-[11px] text-app-2">{sub}</div>}
    </div>
  );
}
